import { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import { useStores } from 'hooks/useStores'
import { createAthleteProfile, createChildAthleteProfile, createCoachProfile } from 'API/profile'
import {
  AUTH_MOBILE_BUTTON_GRADIENT,
  AUTH_MOBILE_BUTTON_GRADIENT_HOVER
} from 'components/Layouts/AuthLayout/constants'
import { PROFILE_TYPE_LABEL, AddableProfileType } from 'utils/profile-eligibility'
import { extractMessage, prefilledNameParts } from './helpers'
import StepYouthForm from './step-youth-form'
import StepPlayerCoachForm from './step-player-coach-form'
import StepPickType from './step-pick-type'

interface Props {
  isOpen: boolean
  onClose: () => void
  addableTypes: AddableProfileType[]
  onCreated: (kind: AddableProfileType) => void
  onConflict?: (kind: AddableProfileType) => void
}

type Step = 'pick' | 'fill'

const AddProfileModal = observer(({ isOpen, onClose, addableTypes, onCreated, onConflict }: Props) => {
  const { authStore, profileStore } = useStores()

  const [step, setStep] = useState<Step>('pick')
  const [selectedType, setSelectedType] = useState<AddableProfileType | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Youth form state
  const [youthFirst, setYouthFirst] = useState('')
  const [youthLast, setYouthLast] = useState('')
  const [youthYear, setYouthYear] = useState('')

  // Reset state and optionally skip to fill when modal opens
  useEffect(() => {
    if (!isOpen) return

    setSubmitting(false)
    setYouthFirst('')
    setYouthLast('')
    setYouthYear('')

    // The modal prefills the new profile's name from userDetail. The LOGIN
    // response carries `id` but NOT firstName/lastName — only /users/me does —
    // so guard on the NAMES being missing, not on `id`. Otherwise a freshly
    // logged-in user (id present, names absent) opens the modal and sees
    // "Account info not loaded". Once names are hydrated this skips the fetch,
    // so reopening the modal won't re-fire /users/me + /users/me/group-members.
    const needsUserDetail = addableTypes.includes('player') || addableTypes.includes('coach')
    const { firstName: loadedFirst, lastName: loadedLast } = prefilledNameParts(authStore.userDetail)
    if (needsUserDetail && !loadedFirst && !loadedLast) {
      authStore.getMyUser().catch(() => undefined)
    }

    // Always start at the picker so the user sees every option (ineligible
    // ones rendered disabled with explanation) — no more auto-skip to fill
    // when only one type is eligible.
    setSelectedType(null)
    setStep('pick')
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectType = (t: AddableProfileType) => {
    setSelectedType(t)
    setStep('fill')
  }

  const handleBack = () => {
    setStep('pick')
    setSelectedType(null)
  }

  const handleSafeClose = () => {
    if (!submitting) onClose()
  }

  const handleSubmit = async () => {
    if (!selectedType) return
    setSubmitting(true)
    try {
      if (selectedType === 'player') {
        const { firstName, lastName } = prefilledNameParts(authStore.userDetail)
        // Store first/last separately (fullName is derived server-side).
        const p = await createAthleteProfile({ firstName, lastName, nickname: firstName })
        // Seed the local cache so the dashboard resolves the new profile on first
        // paint (before loadMyProfiles refetch) — avoids a flash of profiles[0].
        profileStore.upsertAthleteProfile(p)
        await authStore.switchToAthleteProfile(p.id)
        onCreated('player')
        onClose()
      } else if (selectedType === 'coach') {
        const { firstName, lastName } = prefilledNameParts(authStore.userDetail)
        const displayName = `${firstName} ${lastName}`.trim()
        const c = await createCoachProfile(displayName)
        profileStore.setCoachProfile(c)
        await authStore.switchToCoachProfile(c.id)
        onCreated('coach')
        onClose()
      } else {
        const dob = `${youthYear}-01-01`
        // Store first/last + dob separately (fullName is derived server-side).
        const p = await createChildAthleteProfile({
          firstName: youthFirst.trim(),
          lastName: youthLast.trim(),
          nickname: youthFirst.trim(),
          dob
        })
        profileStore.upsertAthleteProfile(p)
        await authStore.switchToAthleteProfile(p.id)
        onCreated('youth')
        onClose()
      }
    } catch (err: unknown) {
      const status = (err as any)?.response?.status
      if (status === 409) {
        toast.error('That profile already exists. Refreshing your profiles.')
        onConflict?.(selectedType)
        onClose()
      } else {
        toast.error(extractMessage(err) ?? 'Failed to create profile. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const { firstName, lastName } = prefilledNameParts(authStore.userDetail)
  const userDetailMissing = !firstName && !lastName

  const youthSubmitEnabled =
    youthFirst.trim().length > 0 &&
    youthLast.trim().length > 0 &&
    /^\d{4}$/.test(youthYear)

  const renderPick = () => (
    <StepPickType addableTypes={addableTypes} onSelect={handleSelectType} />
  )

  const renderPlayerCoach = () => (
    <StepPlayerCoachForm
      firstName={firstName}
      lastName={lastName}
      userDetailMissing={userDetailMissing}
    />
  )

  const renderYouth = () => (
    <StepYouthForm
      firstName={youthFirst}
      lastName={youthLast}
      year={youthYear}
      onFirstNameChange={setYouthFirst}
      onLastNameChange={setYouthLast}
      onYearChange={setYouthYear}
    />
  )

  const renderFill = () => {
    if (selectedType === 'player' || selectedType === 'coach') return renderPlayerCoach()
    if (selectedType === 'youth') return renderYouth()
    return null
  }

  const isSubmitDisabled = (() => {
    if (submitting) return true
    if (selectedType === 'player' || selectedType === 'coach') return userDetailMissing
    if (selectedType === 'youth') return !youthSubmitEnabled
    return true
  })()

  const submitLabel = (() => {
    if (selectedType === 'player') return 'Create Player profile'
    if (selectedType === 'coach') return 'Create Coach profile'
    return 'Create Youth profile'
  })()

  const modalTitle = step === 'pick'
    ? 'Add profile'
    : `Create ${selectedType ? `${PROFILE_TYPE_LABEL[selectedType]} profile` : 'profile'}`

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleSafeClose}
      closeOnEsc={!submitting}
      closeOnOverlayClick={!submitting}
      isCentered
    >
      <ModalOverlay />
      <ModalContent bg="#0C1421" color="white">
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalCloseButton
          isDisabled={submitting}
          bg="transparent"
          color="rgba(244, 248, 251, 0.55)"
          _hover={{ bg: 'transparent', color: 'white' }}
          _focus={{ bg: 'transparent', boxShadow: 'none' }}
          _focusVisible={{ bg: 'transparent', boxShadow: 'none' }}
          _active={{ bg: 'transparent' }}
        />
        <ModalBody>{step === 'pick' ? renderPick() : renderFill()}</ModalBody>
        {step === 'fill' && (
          <ModalFooter>
            <HStack spacing={3} w="full" justify="flex-end">
              <Button
                variant="ghost"
                color="white"
                border="none"
                bg="transparent"
                _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                _active={{ bg: 'rgba(255,255,255,0.1)' }}
                _focus={{ boxShadow: 'none' }}
                onClick={handleBack}
                isDisabled={submitting}
              >
                Back
              </Button>
              <Button
                bg={AUTH_MOBILE_BUTTON_GRADIENT}
                color="white"
                borderRadius="999px"
                border="none"
                _hover={{ bg: AUTH_MOBILE_BUTTON_GRADIENT_HOVER }}
                _active={{ opacity: 0.92 }}
                _focus={{ boxShadow: 'none' }}
                onClick={handleSubmit}
                isLoading={submitting}
                isDisabled={isSubmitDisabled}
              >
                {submitLabel}
              </Button>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
})

export default AddProfileModal
